import {environment} from '../environments/environment';
import {isDevMode} from "@angular/core";
import {StoreModule} from "@ngrx/store";
import {StoreDevtoolsModule} from "@ngrx/store-devtools";
import {AUTH_FEATURE_KEY, authReducer, AuthState} from "./auth/store/auth.reducer";
import {DATA_FEATURE_KEY, dataReducer, DataState} from "./store/data.reducer";
import {AUTHORITY_FEATURE_KEY, authorityReducer, AuthorityState} from "./auth/store/authority/authority.reducer";
import {TAG_FEATURE_KEY, tagReducer, TagState} from "./tag/store/tag.reducer";
import {ANALYTICS_FEATURE_KEY, analyticsReducer, AnalyticsState} from "./analytics/store/analytics.reducer";
import {CLIENT_FEATURE_KEY, clientReducer, ClientState} from "./client/store/client.reducer";
import {STATION_FEATURE_KEY, stationReducer, StationState} from "./station/store/station.reducer";
import {USERS_FEATURE_KEY, userReducer, UsersState} from "./user/store/user.reducer";
import {MEMBER_FEATURE_KEY, memberReducer, MemberState} from "./member/store/member.reducer";
import {ASSIGNMENT_FEATURE_KEY, assignmentReducer, AssignmentState} from "./assignment/store/assignment.reducer";
import {PROGRAM_FEATURE_KEY, programReducer, ProgramState} from "./program/store/program.reducer";
import {SESSION_FEATURE_KEY, sessionReducer, SessionState} from "./session/store/session.reducer";
import {EVENT_FEATURE_KEY, eventReducer, EventState} from "./event/store/event.reducer";
import {PATHOLOGY_FEATURE_KEY, pathologyReducer, PathologyState} from "./pathology/store/pathology.reducer";
import {EffectsModule} from "@ngrx/effects";
import {StorageEffects} from "./store/storage.effects";
import {DataEffects} from "./store/data.effects";
import {AuthorityEffects} from "./auth/store/authority/authority.effects";
import {AuthEffects} from "./auth/store/auth.effects";
import {TagEffects} from "./tag/store/tag.effects";
import {AnalyticsEffects} from "./analytics/store/analytics.effects";
import {ClientEffects} from "./client/store/client.effects";
import {StationEffects} from "./station/store/station.effects";
import {UserEffects} from "./user/store/user.effects";
import {MemberEffects} from "./member/store/member.effects";
import {AssignmentEffects} from "./assignment/store/assignment.effects";
import {ProgramEffects} from "./program/store/program.effects";
import {ProgramSessionEffects} from "./program/store/session/session.effects";
import {SessionEffects} from "./session/store/session.effects";
import {EventEffects} from "./event/store/event.effects";
import {ExerciseState} from "./exercise/store/exercise.reducer";
import {
  EVALUATION_SURVEY_FEATURE_KEY,
  evaluationSurveyReducer,
  EvaluationSurveyState,
} from "./evaluation-survey/store/evaluation-survey.reducer";
import {EvaluationSurveyEffects} from "./evaluation-survey/store/evaluation-survey.effects";

/**
 * Represents the overall application state, consolidating multiple state
 * slices that represent different domains of the application.
 *
 * Each property corresponds to a specific domain's state.
 */
export interface ApplicationState {
  auth: AuthState;
  data: DataState;
  authorities: AuthorityState;
  tags: TagState;
  analytics: AnalyticsState;
  exercise: ExerciseState;
  client: ClientState;
  station: StationState;
  users: UsersState;
  members: MemberState;
  assignments: AssignmentState;
  programs: ProgramState;
  sessions: SessionState;
  events: EventState;
  pathology: PathologyState;
  evaluationSurvey: EvaluationSurveyState;
}

/**
 * AppStoreModule is an array containing the configuration for the application's state management using NgRx.
 *
 * It includes the following:
 *
 * 1. StoreModule.forRoot:
 *    - Defines the root-level state and its associated reducers.
 *    - The state is partitioned into multiple slices, each managed by a specific reducer.
 *      - `auth`: Manages authentication-related state.
 *      - `data`: Handles general application data.
 *      - `authorities`: Manages state related to user roles and permissions.
 *      - `tags`: Handles tag-related state.
 *      - `client`: Handles client-related state.
 *      - `station`: Manages station-related state.
 *      - `users`: Handles user-related state.
 *      - `members`: Manages member-related state.
 *      - `assignments`: Handles state related to assignments.
 *      - `programs`: Manages state for programs.
 *      - `sessions`: Handles program session-related state.
 *      - `pathologies`: Manages state for pathologies or diagnoses.
 *
 * 2. EffectsModule.forRoot:
 *    - Configures side-effect handlers for each slice of the application's state.
 *    - Provides a mechanism to handle external interactions or async operations related to the application's state updates.
 *      - `StorageEffects`: Handles effects related to storage activities.
 *      - `DataEffects`: Manages effects for arbitrary data operations.
 *      - `AuthorityEffects`: Handles effects for user role or authority operations.
 *      - `TagEffects`: Manages tag-related effects.
 *      - `ClientEffects`: Manages client operations effects.
 *      - `StationEffects`: Manages station-related effects.
 *      - `UserEffects`: Handles user-related effects.
 *      - `MemberEffects`: Handles effects for member operations.
 *      - `AssignmentEffects`: Manages effects related to assignments.
 *      - `ProgramEffects`: Handles programs-related effects.
 *      - `ProgramSessionEffects`: Manages effects specific to sessions within programs.
 *      - `SessionEffects`: Handles standalone session-related effects.
 */
export const AppStoreModule = [
  StoreModule.forRoot({
    [AUTH_FEATURE_KEY]: authReducer,
    [DATA_FEATURE_KEY]: dataReducer,
    [AUTHORITY_FEATURE_KEY]: authorityReducer,
    [TAG_FEATURE_KEY]: tagReducer,
    [ANALYTICS_FEATURE_KEY]: analyticsReducer,
    [CLIENT_FEATURE_KEY]: clientReducer,
    [STATION_FEATURE_KEY]: stationReducer,
    [USERS_FEATURE_KEY]: userReducer,
    [MEMBER_FEATURE_KEY]: memberReducer,
    [ASSIGNMENT_FEATURE_KEY]: assignmentReducer,
    [PROGRAM_FEATURE_KEY]: programReducer,
    [SESSION_FEATURE_KEY]: sessionReducer,
    [EVENT_FEATURE_KEY]: eventReducer,
    [PATHOLOGY_FEATURE_KEY]: pathologyReducer,
    [EVALUATION_SURVEY_FEATURE_KEY]: evaluationSurveyReducer }),
  EffectsModule.forRoot([
    StorageEffects,
    DataEffects,
    AuthEffects,
    AuthorityEffects,
    TagEffects,
    AnalyticsEffects,
    ClientEffects,
    StationEffects,
    UserEffects,
    MemberEffects,
    AssignmentEffects,
    ProgramEffects,
    ProgramSessionEffects,
    SessionEffects,
    EventEffects,
    EvaluationSurveyEffects,
  ]),
];
if (!environment.production) {
  // Instrumentation must be imported after importing StoreModule
  AppStoreModule.push(StoreDevtoolsModule.instrument({
    maxAge: 25, // Retains last 25 states
    logOnly: !isDevMode(), // Restrict extension to log-only mode
    autoPause: true, // Pauses recording actions and state changes when the extension window is not open
    trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
    traceLimit: 75, // Maximum stack trace frames to be stored (in case trace option was provided as true)
    connectInZone: true // If set to true, the connection is established within the Angular zone
  }));
}
